<template>
  <v-card outlined>
    <v-card-title>
      Top growers
      <v-spacer />
      <v-btn-toggle
        v-model="rangePreset"
        mandatory
        dense
        color="primary"
        class="mr-3"
      >
        <v-btn value="24h">24h</v-btn>
        <v-btn value="7d">7j</v-btn>
        <v-btn value="30d">30j</v-btn>
      </v-btn-toggle>
      <v-btn icon @click="reload" :loading="loading">
        <v-icon>{{ icons.mdiRefresh }}</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle class="pb-0 caption">
      Comparaison du 1ᵉʳ et du dernier snapshot dans la fenêtre. Tri par
      ΔRSS décroissant.
      <span v-if="meta.first_snapshot_id && meta.last_snapshot_id">
        — snapshots #{{ meta.first_snapshot_id }} → #{{ meta.last_snapshot_id }}
      </span>
    </v-card-subtitle>

    <v-card-text v-if="loading && items.length === 0" class="text-center py-6">
      <v-progress-circular indeterminate color="primary" />
    </v-card-text>
    <v-card-text v-else-if="items.length === 0" class="text--secondary py-6 text-center">
      Aucune donnée pour la période (besoin d'au moins 2 snapshots).
    </v-card-text>
    <v-data-table
      v-else
      :items="items"
      :headers="headers"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="top-growers-table"
    >
      <template #item.status="{ item }">
        <v-chip
          x-small
          :color="statusColor(item.status)"
          dark
          class="text-uppercase"
        >
          {{ statusLabel(item.status) }}
        </v-chip>
      </template>
      <template #item.first_rss_mb="{ item }">
        {{ formatMb(item.first_rss_mb) }}
      </template>
      <template #item.last_rss_mb="{ item }">
        {{ formatMb(item.last_rss_mb) }}
      </template>
      <template #item.delta_mb="{ item }">
        <span :class="deltaClass(item.delta_mb)">
          {{ formatDelta(item.delta_mb) }}
        </span>
      </template>
      <template #item.name="{ item }">
        <span :title="item.cmdline" class="grower-name">
          <b>{{ item.name }}</b>
          <span class="text--secondary"> · uid {{ item.uid }}</span>
        </span>
      </template>
      <template #item.instances="{ item }">
        {{ item.instance_count_first }} → {{ item.instance_count_last }}
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import axios from "@axios";
import { mdiRefresh } from "@mdi/js";

const PRESETS = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
};

const STATUS_COLOR = {
  appeared: "success",
  grew: "warning",
  disappeared: "grey",
  shrunk: "info",
  stable: "secondary",
};

const STATUS_LABEL = {
  appeared: "Apparu",
  grew: "Grandi",
  disappeared: "Disparu",
  shrunk: "Réduit",
  stable: "Stable",
};

export default {
  name: "TopGrowersCard",
  data() {
    return {
      rangePreset: "24h",
      loading: false,
      items: [],
      meta: { first_snapshot_id: null, last_snapshot_id: null },
      icons: { mdiRefresh },
      headers: [
        { text: "Process", value: "name", sortable: true },
        { text: "First RSS", value: "first_rss_mb", sortable: true, align: "end" },
        { text: "Last RSS", value: "last_rss_mb", sortable: true, align: "end" },
        { text: "Δ RSS", value: "delta_mb", sortable: true, align: "end" },
        { text: "Instances", value: "instances", sortable: false, align: "end" },
        { text: "Status", value: "status", sortable: true, align: "center" },
      ],
    };
  },
  watch: {
    rangePreset() {
      this.reload();
    },
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const days = PRESETS[this.rangePreset] || 1;
        const to = new Date();
        const from = new Date(to.getTime() - days * 86400 * 1000);
        const { data } = await axios.get("/system/ram-snapshots/top-growers", {
          params: {
            from_date: from.toISOString(),
            to_date: to.toISOString(),
            top: 30,
          },
        });
        this.items = data.items || [];
        this.meta = {
          first_snapshot_id: data.first_snapshot_id,
          last_snapshot_id: data.last_snapshot_id,
        };
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    formatMb(v) {
      if (v == null) return "—";
      if (v >= 1024) return `${(v / 1024).toFixed(2)} Go`;
      return `${v} Mo`;
    },
    formatDelta(v) {
      if (v == null) return "—";
      const sign = v > 0 ? "+" : "";
      return `${sign}${this.formatMb(v)}`;
    },
    deltaClass(v) {
      if (v > 100) return "delta-strong-growth";
      if (v > 0) return "delta-growth";
      if (v < -100) return "delta-strong-shrink";
      if (v < 0) return "delta-shrink";
      return "";
    },
    statusColor(s) {
      return STATUS_COLOR[s] || "secondary";
    },
    statusLabel(s) {
      return STATUS_LABEL[s] || s;
    },
  },
};
</script>

<style scoped>
.grower-name {
  display: inline-block;
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.delta-strong-growth {
  color: #d84315;
  font-weight: 600;
}
.delta-growth {
  color: #ef6c00;
}
.delta-strong-shrink {
  color: #2e7d32;
  font-weight: 600;
}
.delta-shrink {
  color: #43a047;
}
</style>
